import React, { useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import BulkActionBar from '../components/BulkActionBar';
import ConfirmDialog from '../components/ConfirmDialog';
import DataTable, { type Column } from '../components/DataTable';
import SlidePanel from '../components/SlidePanel';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useAdminInvoices } from '../hooks/useAdminApi';
import { useQueryClient } from '@tanstack/react-query';
import type { Invoice } from '@rule42/billing-types';

function formatDate(value: Date | string | undefined): string {
  if (!value) {
    return '—';
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString();
}

function formatCurrency(amountMinorUnits: number, currency: string): string {
  return `${currency.toUpperCase()} ${(amountMinorUnits / 100).toFixed(2)}`;
}

function StatusBadge({ value }: { value: string }): React.JSX.Element {
  const tone = value === 'paid'
    ? 'bg-emerald-100 text-emerald-800'
    : value === 'open'
      ? 'bg-amber-100 text-amber-800'
      : value === 'uncollectible'
        ? 'bg-rose-100 text-rose-700'
        : 'bg-slate-100 text-slate-700';
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${tone}`}>{value}</span>;
}

export default function InvoicesPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'open' | 'paid' | 'void' | 'uncollectible'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkVoidOpen, setBulkVoidOpen] = useState(false);
  const [bulkVoiding, setBulkVoiding] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState<Invoice | null>(null);
  const { data, isLoading, error } = useAdminInvoices({
    limit: 50,
    ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
  });

  const rows = data?.data ?? [];
  const openAmount = rows.reduce((sum, row) => sum + row.amountRemaining, 0);
  const paidCount = rows.filter((row) => row.status === 'paid').length;

  const columns: Column<Invoice>[] = [
    {
      key: 'invoice',
      header: 'Invoice',
      render: (row) => (
        <div>
          <p className="font-medium text-slate-900">{row.id}</p>
          <p className="text-xs text-slate-500">Customer {row.customerId}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge value={row.status} />,
    },
    {
      key: 'amounts',
      header: 'Amounts',
      render: (row) => (
        <div className="text-slate-700">
          <p>Due {formatCurrency(row.amountDue, row.currency)}</p>
          <p className="text-xs text-slate-500">Paid {formatCurrency(row.amountPaid, row.currency)} · Remaining {formatCurrency(row.amountRemaining, row.currency)}</p>
        </div>
      ),
    },
    {
      key: 'dates',
      header: 'Dates',
      render: (row) => (
        <div className="text-slate-700">
          <p>Created {formatDate(row.createdAt)}</p>
          <p className="text-xs text-slate-500">Due {formatDate(row.dueDate)}</p>
        </div>
      ),
    },
    {
      key: 'provider',
      header: 'Provider Ref',
      render: (row) => <span className="text-slate-600">{row.providerInvoiceId ?? '—'}</span>,
    },
  ];

  return (
    <AdminLayout title="Invoices">
      <div className="space-y-6">
        <section className="rounded-[28px] border border-white/70 bg-white/85 p-6 shadow-[0_30px_90px_-50px_rgba(15,23,42,0.7)] backdrop-blur">
          <div className="grid gap-5 lg:grid-cols-[1.4fr_0.9fr] lg:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">Revenue operations</p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Invoices workspace</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Review invoice state, balances due, and provider identifiers without dropping to the raw billing API.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Visible slice</p>
                <p className="mt-1 text-2xl font-semibold text-slate-950">{rows.length}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Paid in slice</p>
                <p className="mt-1 text-2xl font-semibold text-slate-950">{paidCount}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Open balance</p>
                <p className="mt-1 text-2xl font-semibold text-slate-950">{formatCurrency(openAmount, rows[0]?.currency ?? 'usd')}</p>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-slate-700" htmlFor="invoice-status-filter">Status</label>
            <select
              id="invoice-status-filter"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}
              className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700"
            >
              <option value="all">All invoices</option>
              <option value="draft">Draft</option>
              <option value="open">Open</option>
              <option value="paid">Paid</option>
              <option value="void">Void</option>
              <option value="uncollectible">Uncollectible</option>
            </select>
          </div>
        </section>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error.message}
          </div>
        )}

        {selected.size > 0 && (
          <BulkActionBar count={selected.size} label="invoice" onDelete={() => setBulkVoidOpen(true)} onClear={() => setSelected(new Set())} />
        )}

        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          emptyMessage="No invoices found."
          selectable
          selectedKeys={selected}
          onSelectionChange={setSelected}
          onRowClick={(row) => setDetailInvoice(row)}
        />
      </div>

      {/* Invoice Detail Slide Panel */}
      <SlidePanel
        open={detailInvoice !== null}
        onClose={() => setDetailInvoice(null)}
        size="md"
        title={detailInvoice ? `Invoice #${detailInvoice.id.slice(0, 8)}` : ''}
        subtitle={detailInvoice ? `Customer: ${detailInvoice.customerId.slice(0, 8)}...` : undefined}
        category="Invoice Detail"
      >
        {detailInvoice && (
          <div className="px-6 py-6 space-y-4">
            <div className="grid gap-3 grid-cols-2">
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Status</p>
                <p className="mt-1.5"><StatusBadge value={detailInvoice.status} /></p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Amount</p>
                <p className="mt-1.5 font-semibold text-slate-900">{formatCurrency(detailInvoice.amountDue, detailInvoice.currency)}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Due Date</p>
                <p className="mt-1.5 text-sm text-slate-700">{formatDate(detailInvoice.dueDate)}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-[10px] uppercase tracking-wider text-slate-500">Created</p>
                <p className="mt-1.5 text-sm text-slate-700">{formatDate(detailInvoice.createdAt)}</p>
              </div>
            </div>
            <div className="rounded-xl border border-slate-100 bg-white p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Invoice ID</p>
              <p className="mt-1 font-mono text-xs text-slate-600">{detailInvoice.id}</p>
              {detailInvoice.providerInvoiceId && (
                <p className="mt-1 font-mono text-xs text-cyan-600">Stripe: {detailInvoice.providerInvoiceId}</p>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-3">
              {(detailInvoice.status === 'open' || detailInvoice.status === 'draft') && (
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${apiBaseUrl}/admin/invoices/${detailInvoice.id}/void`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (res.ok) {
                        setDetailInvoice(null);
                        void queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
                      }
                    } catch { /* ignore */ }
                  }}
                  className="flex-1 rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  Void Invoice
                </button>
              )}
              {(detailInvoice.status === 'open' || detailInvoice.status === 'draft') && (
                <button
                  onClick={async () => {
                    try {
                      const res = await fetch(`${apiBaseUrl}/admin/invoices/${detailInvoice.id}/pay`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (res.ok) {
                        setDetailInvoice(null);
                        void queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
                      }
                    } catch { /* ignore */ }
                  }}
                  className="flex-1 rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700"
                >
                  Pay Invoice
                </button>
              )}
            </div>
          </div>
        )}
      </SlidePanel>

      <ConfirmDialog
        open={bulkVoidOpen}
        title={`Void ${selected.size} Invoice${selected.size !== 1 ? 's' : ''}`}
        message={<div><p>Void <strong>{selected.size}</strong> invoice{selected.size !== 1 ? 's' : ''}?</p><p className="mt-2 text-amber-700 font-medium">Voided invoices cannot be collected.</p></div>}
        confirmLabel="Void All"
        cancelLabel="Cancel"
        onConfirm={async () => {
          setBulkVoiding(true);
          try {
            await Promise.all([...selected].map((id) =>
              fetch(`${apiBaseUrl}/admin/invoices/${id}/void`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
              })
            ));
            setSelected(new Set());
            setBulkVoidOpen(false);
          } finally {
            setBulkVoiding(false);
          }
        }}
        onCancel={() => setBulkVoidOpen(false)}
        loading={bulkVoiding}
      />
    </AdminLayout>
  );
}
